import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";

import { useAPI, useAuthContext } from "hooks";
import {
  DELIVERED_QUANTITY_TOO_LARGE_MESSAGE,
  deliveredQuantityIsStorable,
  extractAPIRequestErrorMessage,
  uploadFileToS3,
} from "utils";

import { MarkShipmentAsDeliveredModalContent } from "./MarkShipmentAsDeliveredModalContent";
import { ClientAppEvent } from "../../constants";

import type { AxiosError } from "axios";

interface Props {
  onModalClose: VoidFunction;
  modalOpen: boolean;
  shipmentId: number;
  onMarkDeliveredSuccess?: VoidFunction | null;
  quotingUnitOfMeasure: UnitOfMeasureType;
}

export function MarkShipmentAsDeliveredModal(props: Props) {
  const {
    shipmentId,
    modalOpen,
    onModalClose,
    onMarkDeliveredSuccess = null,
    quotingUnitOfMeasure,
  } = props;

  const posthog = usePostHog();
  const { api } = useAPI();
  const { user } = useAuthContext();

  const [notes, setNotes] = useState<string>("");
  const [deliveryTimestamp, setDeliveryTimestamp] = useState<string>(
    DateTime.now().toFormat("yyyy-MM-dd'T'T"),
  );
  const [unitOfMeasureQuantity, setUnitOfMeasureQuantity] = useState<
    number | null
  >(null);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const submit = async () => {
    const pickupAsDateTime = DateTime.fromISO(deliveryTimestamp).toUTC();
    if (pickupAsDateTime > DateTime.now().toUTC()) {
      setSubmissionError("Delivery timestamp cannot be in the future.");
      return;
    }

    if (
      quotingUnitOfMeasure !== "ALL_IN" &&
      (isNil(unitOfMeasureQuantity) || unitOfMeasureQuantity <= 0)
    ) {
      setSubmissionError("Quantity must be greater than 0.");
      return;
    }

    if (!deliveredQuantityIsStorable(unitOfMeasureQuantity)) {
      setSubmissionError(DELIVERED_QUANTITY_TOO_LARGE_MESSAGE);
      return;
    }

    setSubmissionError(null);
    setApiError(null);
    setLoading(true);

    try {
      const pendingFilePks = await Promise.all(
        files.map(async (file) => {
          const urlResponse = await api<CreateDeliveryFileUploadUrlResponse>(
            "post",
            `/shipments/${shipmentId}/create-shipment-delivery-file-upload-url`,
            {
              original_filename: file.name,
              content_type: file.type || "application/octet-stream",
            },
          );

          const { shipment_delivery_file_pk: filePk, upload_url: uploadUrl } =
            urlResponse.data;

          const result = await uploadFileToS3(
            file,
            uploadUrl,
            user?.id?.toString() ?? "",
          );

          if (!result.success) {
            throw new Error(`Failed to upload ${file.name}.`);
          }

          return filePk;
        }),
      );

      await api("post", `/shipments/${shipmentId}/mark-delivered`, {
        notes,
        deliveryTimestamp: DateTime.fromISO(deliveryTimestamp).toUTC().toISO(),
        unitOfMeasureQuantity: isNil(unitOfMeasureQuantity)
          ? null
          : unitOfMeasureQuantity,
        pending_shipment_delivery_file_pks: pendingFilePks,
      });

      posthog?.capture(ClientAppEvent.SHIPMENT_MARKED_AS_DELIVERED, {
        shipment_id: shipmentId,
        has_notes: notes.length > 0,
        file_count: files.length,
      });

      if (onMarkDeliveredSuccess) {
        onMarkDeliveredSuccess();
      }
      onModalClose();
    } catch (e) {
      setApiError(
        extractAPIRequestErrorMessage(
          e as AxiosError,
          "Failed to mark delivered. Please contact us if the issue persists.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      open={modalOpen}
      onClose={onModalClose}
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle component="div">
        <Typography variant="h4">Mark shipment as delivered</Typography>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
        <MarkShipmentAsDeliveredModalContent
          notes={notes}
          onNotesUpdate={(newValue: string) => setNotes(newValue)}
          loading={loading}
          deliveryTimestamp={deliveryTimestamp}
          onDeliveryTimestampUpdate={(newValue: string) =>
            setDeliveryTimestamp(newValue)
          }
          quotingUnitOfMeasure={quotingUnitOfMeasure}
          unitOfMeasureQuantity={unitOfMeasureQuantity}
          onUnitOfMeasureQuantityUpdate={setUnitOfMeasureQuantity}
          files={files}
          onFilesChange={setFiles}
          error={apiError ?? submissionError}
        />
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button autoFocus variant="outlined" onClick={onModalClose}>
            Go Back
          </Button>
          <Button onClick={submit} variant="contained">
            {!apiError ? "Mark Delivered" : "retry"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
